import {
  BPMNEventCatchType,
  BPMNEventInterruptingType,
  BPMNEventMarkerType,
  BPMNEventType,
  getMarkerMarkup,
} from "./KEABPMNEvent";
import { BPMNTaskType, KEABPMNActivity } from "./KEABPMNActivity";
import { GatewayType, getGateWayMarkup, OutlineType } from "./KEABPMNGateway";
import { KEAEPKConnectorType } from "./KEAEPKConnector";
import { StateType } from "./KEAUMLCircleState";
import { LabelPosition } from "./KEAElement";
import { LinkType, MarkerPosition, MarkerType } from "./KEALink";

// Only what is set gets drawn.
export type VariantIcon = {
  rings: { r: number; width: number; dash?: string }[];
  box?: boolean;
  diamond?: boolean;
  marker?: string;
  markerTransform?: string;
  filled?: boolean;
  filledCircle?: number;
  cross?: boolean;
  text?: string;
  line?: string;
  arrow?: MarkerType;
  sourceArrow?: MarkerType;
  dashed?: boolean;
};

export type VariantGroup = {
  key: string;
  labelKey: string;
  options: { value: string; labelKey: string; icon: VariantIcon }[];
  read: (element: any) => string;
  write: (element: any, value: string) => void;
  appliesTo: (element: any) => boolean;
};

const MARKERS = [
  BPMNEventMarkerType.NONE,
  BPMNEventMarkerType.MESSAGE,
  BPMNEventMarkerType.TIMER,
  BPMNEventMarkerType.ERROR,
  BPMNEventMarkerType.ESCALATION,
  BPMNEventMarkerType.CANCEL,
  BPMNEventMarkerType.COMPENSATION,
  BPMNEventMarkerType.LINK,
  BPMNEventMarkerType.SIGNAL,
  BPMNEventMarkerType.MULTIPLE,
  BPMNEventMarkerType.PARALLEL_MULTIPLE,
  BPMNEventMarkerType.TERMINATE,
  BPMNEventMarkerType.CONDITIONAL,
];

const eventVariantGroups: VariantGroup[] = [
  {
    key: "eventType",
    labelKey: "variant_event_type",
    options: [
      { value: BPMNEventType.START, labelKey: "variant_start", icon: { rings: [{ r: 9, width: 1 }] } },
      {
        value: BPMNEventType.INTERMEDIATE,
        labelKey: "variant_intermediate",
        icon: { rings: [{ r: 9, width: 1 }, { r: 6.5, width: 1 }] },
      },
      { value: BPMNEventType.END, labelKey: "variant_end", icon: { rings: [{ r: 9, width: 3 }] } },
    ],
    read: (e) => e.getBPMNEventTyoe(),
    write: (e, v) => e.setBPMNEventType(v as BPMNEventType),
    appliesTo: () => true,
  },
  {
    key: "eventMarker",
    labelKey: "variant_marker",
    options: MARKERS.map((m) => ({
      value: m,
      labelKey: `variant_marker_${m.toLowerCase()}`,
      icon: { rings: [{ r: 9, width: 1 }], marker: getMarkerMarkup(m).d, markerTransform: "translate(-5 -5)" },
    })),
    read: (e) => e.get("eventMarker") ?? BPMNEventMarkerType.NONE,
    write: (e, v) => e.setMarker(v as BPMNEventMarkerType),
    appliesTo: () => true,
  },
  {
    key: "eventCatchType",
    labelKey: "variant_catch",
    options: [
      {
        value: BPMNEventCatchType.CATCHING,
        labelKey: "variant_catching",
        icon: {
          rings: [{ r: 9, width: 1 }],
          marker: getMarkerMarkup(BPMNEventMarkerType.MESSAGE).d,
          markerTransform: "translate(-5 -5)",
        },
      },
      {
        value: BPMNEventCatchType.THROWING,
        labelKey: "variant_throwing",
        icon: {
          rings: [{ r: 9, width: 1 }],
          marker: getMarkerMarkup(BPMNEventMarkerType.MESSAGE).d,
          markerTransform: "translate(-5 -5)",
          filled: true,
        },
      },
    ],
    read: (e) => e.getBPMNEventCatchType() ?? BPMNEventCatchType.CATCHING,
    write: (e, v) => e.setBPMNEventCatchType(v as BPMNEventCatchType),
    appliesTo: (e) => e.getBPMNEventTyoe() !== BPMNEventType.START,
  },
  {
    key: "eventInterruptingType",
    labelKey: "variant_interrupting",
    options: [
      {
        value: BPMNEventInterruptingType.INTERRUPTING,
        labelKey: "variant_interrupting_yes",
        icon: { rings: [{ r: 9, width: 1 }] },
      },
      {
        value: BPMNEventInterruptingType.NON_INTERRUPTING,
        labelKey: "variant_interrupting_no",
        icon: { rings: [{ r: 9, width: 1, dash: "3 2" }] },
      },
    ],
    read: (e) => e.getBPMNEventInterruptingType() ?? BPMNEventInterruptingType.INTERRUPTING,
    write: (e, v) => e.setBPMNEventInterruptingType(v as BPMNEventInterruptingType),
    appliesTo: (e) => e.getBPMNEventTyoe() !== BPMNEventType.END,
  },
];

const TASK_TYPES = [
  BPMNTaskType.NONE,
  BPMNTaskType.USER,
  BPMNTaskType.SERVICE,
  BPMNTaskType.SEND,
  BPMNTaskType.RECEIVE,
  BPMNTaskType.MANUAL,
  BPMNTaskType.SCRIPT,
  BPMNTaskType.BUSINESS_RULE,
];

const toggle = (
  key: string,
  labelKey: string,
  read: (a: KEABPMNActivity) => boolean,
  write: (a: KEABPMNActivity, on: boolean) => void,
  onIcon: VariantIcon,
): VariantGroup => ({
  key,
  labelKey,
  options: [
    { value: "false", labelKey: "variant_off", icon: { rings: [], box: true } },
    { value: "true", labelKey, icon: onIcon },
  ],
  read: (a: any) => String(read(a)),
  write: (a: any, v: string) => write(a, v === "true"),
  appliesTo: () => true,
});

const activityVariantGroups: VariantGroup[] = [
  {
    key: "taskType",
    labelKey: "variant_task_type",
    options: TASK_TYPES.map((t) => ({
      value: t,
      labelKey: `variant_task_${t.toLowerCase()}`,
      icon: { rings: [], box: true, marker: KEABPMNActivity.getTaskTypePath(t), markerTransform: "translate(-5 -5)" },
    })),
    read: (a: any) => a.getTaskType() ?? BPMNTaskType.NONE,
    write: (a: any, v: string) => a.setTaskType(v as BPMNTaskType),
    appliesTo: () => true,
  },
  toggle("isLoop", "variant_loop", (a) => a.getIsLoop(), (a, on) => a.setIsLoop(on), {
    rings: [],
    box: true,
    marker: "M 5 8 A 4 4 0 1 1 8 4 M 8 1 L 8 4 L 5 4",
    markerTransform: "translate(-5 -5)",
  }),
  toggle("isParallel", "variant_parallel", (a) => a.getIsParallel(), (a, on) => a.setIsParallel(on), {
    rings: [],
    box: true,
    marker: "M 2 1 L 2 9 M 5 1 L 5 9 M 8 1 L 8 9",
    markerTransform: "translate(-5 -5)",
  }),
  toggle("isSequential", "variant_sequential", (a) => a.getIsSequential(), (a, on) => a.setIsSequential(on), {
    rings: [],
    box: true,
    marker: "M 1 2 L 9 2 M 1 5 L 9 5 M 1 8 L 9 8",
    markerTransform: "translate(-5 -5)",
  }),
  toggle("isAdHoc", "variant_adhoc", (a) => a.getIsAdHoc(), (a, on) => a.setIsAdHoc(on), {
    rings: [],
    box: true,
    marker: "M 1 6 Q 3 2 5 5 Q 7 8 9 4",
    markerTransform: "translate(-5 -5)",
  }),
  toggle("isCompensation", "variant_compensation", (a) => a.getIsCompensation(), (a, on) => a.setIsCompensation(on), {
    rings: [],
    box: true,
    marker: "M 5 1 L 5 9 L 1 5 Z M 9 1 L 9 9 L 5 5 Z",
    markerTransform: "translate(-5 -5)",
  }),
];

const GATEWAY_TYPES = [
  GatewayType.NONE,
  GatewayType.EXLUSIVE,
  GatewayType.PARALLEL,
  GatewayType.INCLUSIVE,
  GatewayType.EVENT_BASED,
  GatewayType.EVENT_PARALLEL,
  GatewayType.COMPLEX,
];

const gatewayVariantGroups: VariantGroup[] = [
  {
    key: "gatewayType",
    labelKey: "variant_gateway_type",
    options: GATEWAY_TYPES.map((t) => ({
      value: t,
      labelKey: `variant_gateway_${t.toLowerCase()}`,
      icon: {
        rings: [],
        diamond: true,
        marker: getGateWayMarkup(t).d,
        markerTransform: "scale(0.7)",
        filled: getGateWayMarkup(t).fill !== "none",
      },
    })),
    read: (g: any) => g.getGatewayType() ?? GatewayType.NONE,
    write: (g: any, v: string) => g.setGatewayType(v as GatewayType),
    appliesTo: () => true,
  },
  {
    key: "outlineType",
    labelKey: "variant_outline",
    options: [
      { value: OutlineType.NONE, labelKey: "variant_outline_none", icon: { rings: [], diamond: true } },
      { value: OutlineType.SINGLE, labelKey: "variant_outline_single", icon: { rings: [{ r: 4.5, width: 1 }], diamond: true } },
      {
        value: OutlineType.DOUBLE,
        labelKey: "variant_outline_double",
        icon: { rings: [{ r: 3, width: 1 }, { r: 5, width: 1 }], diamond: true },
      },
    ],
    read: (g: any) => g.getOutlineType() ?? OutlineType.NONE,
    write: (g: any, v: string) => g.setOutlineType(v as OutlineType),
    appliesTo: () => true,
  },
];

const epkConnectorVariantGroups: VariantGroup[] = [
  {
    key: "connectorType",
    labelKey: "variant_connector_type",
    options: [
      { value: KEAEPKConnectorType.AND, labelKey: "variant_connector_and", icon: { rings: [{ r: 9, width: 1 }], text: "\u2227" } },
      { value: KEAEPKConnectorType.OR, labelKey: "variant_connector_or", icon: { rings: [{ r: 9, width: 1 }], text: "\u2228" } },
      { value: KEAEPKConnectorType.XOR, labelKey: "variant_connector_xor", icon: { rings: [{ r: 9, width: 1 }], text: "X" } },
    ],
    read: (c: any) => c.get("connectorType") ?? KEAEPKConnectorType.AND,
    write: (c: any, v: string) => c.setConnectorType(v as KEAEPKConnectorType),
    appliesTo: () => true,
  },
];

const umlStateVariantGroups: VariantGroup[] = [
  {
    key: "stateType",
    labelKey: "variant_state_type",
    options: [
      { value: StateType.INITIAL, labelKey: "variant_state_initial", icon: { rings: [{ r: 7, width: 1 }], filledCircle: 7 } },
      { value: StateType.FINAL, labelKey: "variant_state_final", icon: { rings: [{ r: 9, width: 1 }], filledCircle: 5 } },
      { value: StateType.EXIT, labelKey: "variant_state_exit", icon: { rings: [{ r: 8, width: 1 }], cross: true } },
    ],
    read: (c: any) => c.get("stateType") ?? StateType.INITIAL,
    write: (c: any, v: string) => c.setStateType(v as StateType),
    appliesTo: () => true,
  },
];

// Containers (pool, region, frame) and add-ons (activation bar, destruction, port, Petri marker) are placed,
// not attached by an edge, so they are neither a valid append target nor a source an edge may start from.
const NOT_APPENDABLE = new Set([
  "kea.BPMNSwimlane",
  "kea.UMLArea",
  "kea.UMLFrame",
  "kea.UMLActivityBar",
  "kea.UMLDestruction",
  "kea.UMLPort",
  "kea.PNMarker",
]);

export const isAppendable = (type: string): boolean => !NOT_APPENDABLE.has(type);

export const canStartEdge = (type: string): boolean => !NOT_APPENDABLE.has(type);

const VARIANT_TYPES = new Set([
  "kea.BPMNEvent",
  "kea.BPMNActivity",
  "kea.BPMNGateway",
  "kea.EPKConnector",
  "kea.UMLCircleState",
  "kea.UMLClass",
]);

export const hasElementVariants = (type: string): boolean => VARIANT_TYPES.has(type);

const umlClassVariantGroups: VariantGroup[] = [
  {
    key: "classIdentifier",
    labelKey: "variant_stereotype",
    options: [
      { value: "Class", labelKey: "variant_stereotype_class", icon: { rings: [], text: "C" } },
      { value: "Interface", labelKey: "variant_stereotype_interface", icon: { rings: [], text: "I" } },
      { value: "Enumeration", labelKey: "variant_stereotype_enumeration", icon: { rings: [], text: "E" } },
    ],
    read: (c: any) => c.getClassIdentifier(),
    write: (c: any, v: string) => c.setClassIdentifier(v),
    appliesTo: () => true,
  },
];

const labelBox = (line: string): VariantIcon => ({ rings: [], box: true, line });

const labelVariantGroups: VariantGroup[] = [
  {
    key: "labelPosition",
    labelKey: "variant_label_position",
    options: [
      { value: LabelPosition.ABOVE, labelKey: "variant_label_above", icon: labelBox("M -7 -9 L 7 -9") },
      { value: LabelPosition.CENTER, labelKey: "variant_label_center", icon: labelBox("M -7 0 L 7 0") },
      { value: LabelPosition.BELOW, labelKey: "variant_label_below", icon: labelBox("M -7 9 L 7 9") },
    ],
    read: (c: any) => c.getLabelPosition(),
    write: (c: any, v: string) => c.setLabelPosition(v as LabelPosition),
    appliesTo: () => true,
  },
  {
    key: "labelUnderline",
    labelKey: "variant_label_underline",
    options: [
      { value: "false", labelKey: "variant_underline_off", icon: { rings: [], text: "A" } },
      { value: "true", labelKey: "variant_underline_on", icon: { rings: [], text: "A", line: "M -5 7 L 5 7" } },
    ],
    read: (c: any) => String(c.isLabelUnderlined?.() ?? false),
    write: (c: any, v: string) => c.setLabelUnderlined(v === "true"),
    appliesTo: (c: any) => c.get("type") === "kea.Ellipse",
  },
];

export const linkVariantsFromTemplate = (templateLinks: any[]): VariantGroup[] => {
  const seen = new Set<string>();
  const options: { value: string; labelKey: string; icon: VariantIcon }[] = [];
  templateLinks.forEach((link) => {
    const style = link.getDasharray?.() ?? LinkType.SOLID;
    const source = link.getMarkerType?.(MarkerPosition.SOURCE_MARKER) ?? MarkerType.NONE;
    const target = link.getMarkerType?.(MarkerPosition.TARGET_MARKER) ?? MarkerType.NONE;
    const value = [style, source, target].join("|");
    if (seen.has(value)) return;
    seen.add(value);
    options.push({
      value,
      labelKey: "variant_link_type",
      icon: { rings: [], line: "M -9 0 L 9 0", dashed: style === LinkType.DASHED, arrow: target, sourceArrow: source },
    });
  });
  if (options.length < 2) return [];
  return [
    {
      key: "linkVariant",
      labelKey: "variant_link_type",
      options,
      read: (l: any) =>
        [
          l.getDasharray?.() ?? LinkType.SOLID,
          l.getMarkerType?.(MarkerPosition.SOURCE_MARKER) ?? MarkerType.NONE,
          l.getMarkerType?.(MarkerPosition.TARGET_MARKER) ?? MarkerType.NONE,
        ].join("|"),
      write: (l: any, v: string) => {
        const [style, source, target] = v.split("|");
        l.setDasharray(style as LinkType);
        l.setMarker(MarkerPosition.SOURCE_MARKER, source as MarkerType);
        l.setMarker(MarkerPosition.TARGET_MARKER, target as MarkerType);
      },
      appliesTo: () => true,
    },
  ];
};

// The four free shapes must match EditPositionLabelModal.
const VARIANT_GROUPS: Record<string, VariantGroup[]> = {
  "kea.BPMNEvent": eventVariantGroups,
  "kea.BPMNActivity": activityVariantGroups,
  "kea.BPMNGateway": gatewayVariantGroups,
  "kea.EPKConnector": epkConnectorVariantGroups,
  "kea.UMLCircleState": umlStateVariantGroups,
  "kea.UMLClass": umlClassVariantGroups,
  "kea.Circle": labelVariantGroups,
  "kea.Ellipse": labelVariantGroups,
  "kea.Polygon": labelVariantGroups,
  "kea.Rectangle": labelVariantGroups,
};

export const variantGroupsForType = (type: string): VariantGroup[] => VARIANT_GROUPS[type] ?? [];
